import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subscription, firstValueFrom, interval, map } from 'rxjs';
import { IMessage, IThread, MessageAuthor } from '@cnt-workspace/models';
import { MessageApiService } from '../backend-api/message-api.service';
import type { IListing } from '../listings/mock-listings.data';
import { AuthService, IPublicUser } from '../auth/auth.service';
import { buildCreatePayloadForExistingChannel, buildCreatePayloadForNewChannel, channelToThread } from './message-real-mapper';

const POLL_MS = 30_000;

/**
 * Real-backed messaging — talks to curbnturf's `/message` API via the local
 * forked `MessageApiService` in `../backend-api/` (see that folder's header
 * for why this is a fork of the real `@curbnturf/message` service rather
 * than the real class directly) — mirrors how `BookingService` injects
 * `../backend-api/reservation-api.service`'s `ReservationApiService` directly.
 *
 * `IThread`/`IMessage` (`@cnt-workspace/models`) stay the public shape every
 * existing consumer (inbox, notifications, navbar/bottom-nav unread badge)
 * already reads — `message-real-mapper.ts` does all `IMessageChannel ↔
 * IThread` translation.
 *
 * Known, deliberate gaps:
 *  - The server's `GET /message?siteId=` filter is a no-op (the service
 *    reads `where.listingId`, which is never populated from `siteId`) — we
 *    only ever query by `userId` and derive listing/reservation linkage
 *    client-side from each returned channel.
 *  - No server-side "system message" concept exists — booking status
 *    transitions are no longer rendered as chat bubbles; they're still
 *    fully visible via the inbox's independent timeline strip/decision card.
 *  - `lastReadAt` is an approximation backfilled from per-message `read`
 *    flags (see `message-real-mapper.ts`'s header comment) — the real model
 *    has no literal read-instant.
 *  - No real-time delivery — freshness is poll-only (`POLL_MS`), per
 *    explicit product decision; real-time is deferred to a later pass.
 *  - Mock-only bookings (ids not starting with `res-`) no longer get an
 *    inbox thread at all — accepted scope-narrowing now that auth/listings
 *    are real and the mock auto-reply that animated those threads is gone.
 */
@Injectable({ providedIn: 'root' })
export class MessageService {
  private platformId = inject<object>(PLATFORM_ID);
  private auth = inject(AuthService);
  private real = inject(MessageApiService);

  private readonly _threads$ = new BehaviorSubject<IThread[]>([]);
  readonly threads$: Observable<IThread[]> = this._threads$.asObservable();
  private pollSub: Subscription | null = null;

  constructor() {
    this.auth.currentUser$.subscribe(user => {
      if (user?.id) {
        void this.refreshNow();
        this.startPolling();
      } else {
        this.stopPolling();
        this._threads$.next([]);
      }
    });
  }

  // ---- Hydration ----------------------------------------------------------

  private startPolling(): void {
    if (!isPlatformBrowser(this.platformId) || this.pollSub) return;
    this.pollSub = interval(POLL_MS).subscribe(() => void this.refreshNow());
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  /** Force an immediate refresh of every channel the current user is on,
   *  outside the standing poll interval — call on `/inbox` mount so the
   *  page doesn't wait for the next tick. */
  async refreshNow(): Promise<void> {
    const userId = this.auth.currentUser?.id;
    if (!userId) return;
    try {
      const channels = await firstValueFrom(this.real.query({ userId }));
      this._threads$.next(channels.map(c => channelToThread(c)));
    } catch (err) {
      console.error('[message] failed to refresh threads', err);
    }
  }

  /** Hydrate one channel's full message history. The bulk `userId` list may
   *  return a lighter summary, so the currently-open thread always fetches
   *  itself directly to stay authoritative. */
  async hydrateThread(threadId: string): Promise<void> {
    const id = Number(threadId);
    if (!Number.isFinite(id)) return;
    try {
      const channel = await firstValueFrom(this.real.fetch(id));
      this.upsertThread(channelToThread(channel));
    } catch (err) {
      console.error('[message] failed to hydrate thread', threadId, err);
    }
  }

  private upsertThread(thread: IThread): void {
    const next = this._threads$.value.filter(t => t.id !== thread.id);
    next.push(thread);
    this._threads$.next(next);
  }

  // ---- Public read API (sync — reads the in-memory cache) ----------------

  /** What role does this user play in this thread? null = no relationship.
   *  Prefers numeric-id comparison (authoritative for real threads) and
   *  falls back to email matching only when id data wasn't populated. */
  roleForUser(t: IThread, email: string): 'guest' | 'host' | null {
    const myId = this.auth.currentUser?.id;
    if (myId != null) {
      if (t.guestUserId === myId) return 'guest';
      if (t.hostUserId === myId) return 'host';
    }
    if (t.guestEmail === email) return 'guest';
    if (t.hostEmail === email) return 'host';
    return null;
  }

  private readKeyFor(t: IThread, email: string): string | null {
    const role = this.roleForUser(t, email);
    if (role === 'guest') return t.guestEmail;
    if (role === 'host') return t.hostEmail;
    return null;
  }

  threadsForUser(email: string): IThread[] {
    return this._threads$.value
      .filter(t => this.roleForUser(t, email) !== null)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  getThread(id: string): IThread | null {
    return this._threads$.value.find(t => t.id === id) ?? null;
  }

  unreadFor$(email: string): Observable<number> {
    return this.threads$.pipe(
      map(list => list.filter(t => this.isUnread(t, email)).length),
    );
  }

  isUnread(t: IThread, email: string): boolean {
    if (t.messages.length === 0) return false;
    const last = t.messages[t.messages.length - 1];
    const role = this.roleForUser(t, email);
    if (!role) return false;
    if (last.author === role) return false;
    const key = this.readKeyFor(t, email);
    if (!key) return false;
    const lastRead = t.lastReadAt[key];
    return !lastRead || lastRead < last.createdAt;
  }

  /** True when the counterparty's lastReadAt is newer than this message's
   *  createdAt — drives the "Seen ✓✓" indicator on outgoing bubbles. */
  hasBeenReadBy(t: IThread, m: IMessage, viewerEmail: string): boolean {
    const viewerRole = this.roleForUser(t, viewerEmail);
    if (!viewerRole) return false;
    const otherKey = viewerRole === 'guest' ? t.hostEmail : t.guestEmail;
    const lastRead = t.lastReadAt?.[otherKey];
    if (!lastRead) return false;
    return Date.parse(lastRead) >= Date.parse(m.createdAt);
  }

  // ---- Mutations (async — real HTTP) --------------------------------------

  /** Best-effort — a failed read receipt is low-stakes, so errors are
   *  logged rather than surfaced to the caller. */
  async markRead(threadId: string, email: string): Promise<void> {
    const t = this.getThread(threadId);
    if (!t || !this.isUnread(t, email)) return;
    const id = Number(threadId);
    if (!Number.isFinite(id)) return;
    try {
      const channel = await firstValueFrom(this.real.read({ id }));
      this.upsertThread(channelToThread(channel));
    } catch (err) {
      console.error('[message] failed to mark thread read', threadId, err);
    }
  }

  /** `author` is kept for call-site compatibility (inbox.component.ts) —
   *  the real backend derives `from` from the auth token, so it's unused
   *  here. Throws on HTTP failure so the caller can surface a real error. */
  async sendMessage(threadId: string, _author: MessageAuthor, body: string): Promise<IMessage | null> {
    const trimmed = body.trim();
    if (!trimmed) return null;
    const t = this.getThread(threadId);
    if (!t) return null;
    const id = Number(threadId);
    if (!Number.isFinite(id)) return null;
    const payload = buildCreatePayloadForExistingChannel(id, trimmed);
    const channel = await firstValueFrom(this.real.create(payload));
    const updated = channelToThread(channel);
    this.upsertThread(updated);
    return updated.messages[updated.messages.length - 1] ?? null;
  }

  /** Starts (or appends to, if a channel already exists) a guest→host
   *  channel for a listing that has no reservation yet — the pre-booking
   *  "Message host" inquiry flow. `hostName` is kept for call-site
   *  compatibility (`message-host-modal.component.ts`) — the real host
   *  name comes back from `channel.listing.user` instead. Throws on HTTP
   *  failure so the caller can surface a real error. */
  async startThreadWithHost(listing: IListing, _hostName: string, guestUser: IPublicUser, body: string): Promise<IThread | null> {
    const trimmed = body.trim();
    if (!trimmed || !guestUser.id) return null;
    const payload = buildCreatePayloadForNewChannel(listing.id, guestUser.id, trimmed);
    const channel = await firstValueFrom(this.real.create(payload));
    const thread = channelToThread(channel);
    this.upsertThread(thread);
    return thread;
  }
}
